import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * Textarea — multi-line input primitive. Mirrors `<Input>`'s shape so a
 * form composed of both reads as one family: same border treatment, same
 * focus ring, same disabled affordance, same size scale.
 *
 *   <Textarea placeholder="Notes" />            // default
 *   <Textarea variant="ghost" />                // borderless, for inline edit
 *   <Textarea size="sm" rows={2} />
 *
 * `<Input>` and `<Textarea>` share the same variant + size names so a
 * field-grouping helper can pass through the prop blindly.
 *
 * Width: the base is `w-full`, exactly matching `<Input>`. The consumer's
 * container dictates how wide the field renders — wrap in `max-w-md` for a
 * typical form field, `max-w-2xl` for a card-body description. Don't
 * hardcode a fixed width on the primitive; it makes inline-edit surfaces
 * (where parents own height + width) jam. Resize is vertical-only on
 * `default` (`resize-y`) and disabled on `ghost` (`resize-none`); horizontal
 * resize is intentionally omitted because it breaks layouts that rely on
 * the parent's width budget.
 */
export const textareaVariants = cva(
  [
    "flex w-full bg-background text-foreground ring-offset-background",
    "transition-all duration-180 ease-smooth-out",
    "placeholder:text-muted-foreground",
    // The focus RING OFFSET lives on the variants that actually draw a ring,
    // not here. When the base owned it, every faceless variant had to REMEMBER
    // to zero it, and three of four forgot: Tailwind emits ring-offset as its
    // own box-shadow, so a transparent field with ring-0 still painted a 2px
    // --background box with no ring inside it. Forgetting to ADD an offset is a
    // flush ring, which is cosmetic; forgetting to ZERO one is a visible box on
    // a field that is meant to be invisible. Putting it on the variants makes
    // the failure mode the harmless one.
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "resize-y",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border border-input rounded-lg focus-visible:ring-offset-2",
        ghost: "border-0 bg-transparent rounded-none px-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none",
      },
      size: {
        sm: "min-h-[60px] px-3 py-2 text-xs",
        default: "min-h-[80px] px-3 py-2 text-sm",
        lg: "min-h-[120px] px-4 py-3 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
    VariantProps<typeof textareaVariants> {}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, size, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(textareaVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Textarea.displayName = "Textarea";
